import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CloseIcon,
  ESPNLogo,
  NFLLogo,
  RedZoneLogo,
  YahooLogo,
} from "../components/CustomIcons";
import sleeperLogo from "../../src/assets/images/sleeper-logo.png";
import {
  addLeagueToFirestore,
  checkSlugExists,
  getLeagueBySlug,
  updateLeagueBasicInfo,
  updateLeagueFundsDistribution,
  updateLeagueProgressStep,
} from "../components/leagueLocker/firestoreQuery";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { validateImageFile } from "../components/utils/imageValidation";
import { resizeImage } from "../components/utils/resizeImage";
import Cropper from "react-easy-crop";
import { cropImageFile } from "../components/utils/cropImage";
import { uploadImage } from "../services/firebase";

const CreateLeaguePage: React.FC = () => {
  // States
  const [leagueName, setLeagueName] = useState("");
  const [leagueUrl, setLeagueUrl] = useState("");
  const [leagueSize, setLeagueSize] = useState("8");
  const [platform, setPlatform] = useState("Not Listed");
  const [isSubmit, setIsSubmit] = useState(false);
  const [leagueId, setLeagueId] = useState<string | null>(null); //Edit mode
  const { leagueSlug } = useParams(); //Edit mode
  const [leagueFunds, setLeagueFunds] = useState<any>(null); //Edit mode
  const [teamJoined, setTeamJoined] = useState(0); // Edit mode
  const [existingBannerImage, setExistingBannerImage] = useState<string | null>(
    null
  ); // Edit mode

  /**
   * States for Image Upload & Cropper
   */
  const [leagueImage, setLeagueImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null); // existingImageUrl from leagueData if present
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const leagueImageRef = useRef<HTMLInputElement | null>(null);
  const [isPublic, setIsPublic] = useState(false);

  // Error States
  const [errors, setErrors] = useState<{
    leagueName?: string;
    leagueUrl?: string;
    leagueSize?: string;
    platform?: string;
  }>({});

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLeagueDetails = async () => {
      if (leagueSlug) {
        try {
          const res = await getLeagueBySlug(leagueSlug);
          console.log("Fetched league data:", res);
          if (res) {
            if (res.createdBy !== user?.uid) {
              navigate("/dashboard");
              return;
            } else {
              setLeagueName(res.leagueName || "");
              setLeagueUrl(res.leagueUrl || "");
              setLeagueSize(res.leagueSize || "8");
              setPlatform(res.platform || "Not Listed");
              setLeagueId(res.id || null);
              setLeagueFunds(res.leagueFunds || null);
              setTeamJoined(res.teams ? res.teams.length : 0);
              setImagePreview(res.bannerImage || null);
              setExistingBannerImage(res.bannerImage || null);
              setIsPublic(res.isPublic || false);
            }
          } else {
            navigate("/dashboard");
          }
        } catch (error) {
          console.error("Error fetching league:", error);
          //setErrorText(error as string);
        }
      }
      //setIsLoading(false);
    };
    fetchLeagueDetails();
  }, [leagueSlug]);

  // Auto slug generator (for leagueName → leagueUrl)
  const generateSlug = (title: string) => {
    let slug = "";
    if (title) {
      slug = title
        .normalize("NFD") // split accents
        .replace(/[\u0300-\u036f]/g, "") // remove accent marks
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-") // non-alphanumerics → hyphen
        .replace(/^-+|-+$/g, ""); // trim leading/trailing hyphen
    }
    return slug;
  };

  const handleLeagueNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLeagueName(value);
    setLeagueUrl(generateSlug(value)); // auto slug from title
    setErrors((prev) => ({ ...prev, leagueName: "" }));
  };

  // Allow manual edits to slug, but clean input
  const handleLeagueUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, "") // allow only lowercase letters, numbers, dash
      .replace(/-+/g, "-"); // collapse multiple dashes into one

    console.log("League URL Changed:", value);
    setLeagueUrl(value);
    setErrors((prev) => ({ ...prev, leagueUrl: "" }));
  };

  const handleLeagueSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (leagueId) {
      if (teamJoined > parseInt(e.target.value)) {
        setErrors((prev) => ({
          ...prev,
          leagueSize: `Already ${teamJoined} teams joined.`,
        }));
        return;
      } else {
        setLeagueSize(e.target.value);
        setErrors((prev) => ({ ...prev, leagueSize: "" }));
      }
    } else {
      setLeagueSize(e.target.value);
      setErrors((prev) => ({ ...prev, leagueSize: "" }));
    }
  };

  const handlePlatformSelect = (selected: string) => {
    setPlatform(selected);
    setErrors((prev) => ({ ...prev, platform: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    setIsSubmit(true);
    e.preventDefault();
    let formErrors: typeof errors = {};

    if (!leagueName.trim()) {
      formErrors.leagueName = "League Name is required";
    }

    if (!leagueUrl.trim()) {
      formErrors.leagueUrl = "League URL is required";
    } else if (leagueUrl.endsWith("-")) {
      formErrors.leagueUrl = "League URL cannot end with a hyphen (-)";
    }

    if (!leagueSize) {
      formErrors.leagueSize = "League Size is required";
    }
    if (!platform) {
      formErrors.platform = "Fantasy Platform is required";
    }

    if (Object.keys(formErrors).length > 0) {
      console.log("Validation Failed:", formErrors);
      setErrors(formErrors);
      return;
    }

    let finalSlug = leagueUrl;
    let leagueData = {
      leagueName,
      leagueUrl: finalSlug,
      leagueSize,
      platform,
      createdBy: user?.uid ?? "",
      createdAt: new Date().toISOString(),
      isPublic,
    };

    if (!leagueSlug && !leagueId) {
      // only check for new leagues
      while (await checkSlugExists(finalSlug)) {
        const match = finalSlug.match(/^(.*?)(?:-(\d+))?$/);
        if (match) {
          const base = match[1];
          const num = match[2] ? parseInt(match[2], 10) : 0;
          finalSlug = `${base}-${num + 1}`;
        }
      }
      setLeagueUrl(finalSlug);
    } else {
      if (finalSlug !== leagueSlug) {
        // slug changed, need to check
        let slugExists = await checkSlugExists(finalSlug);
        if (slugExists) {
          formErrors.leagueUrl =
            "League slug already exists. Please enter another.";
          setErrors(formErrors);
          setIsSubmit(false);
          return;
        }
        setLeagueUrl(finalSlug);
      }
    }

    if (leagueSlug && leagueId) {
      // update existing league - to be implemented
      let bannerImageUrl =
        existingBannerImage && imagePreview ? existingBannerImage : "";
      if (leagueImage) {
        try {
          bannerImageUrl = await uploadImage(leagueImage);
        } catch (error) {
          console.error("Error uploading image:", error);
        }
      }

      await updateLeagueBasicInfo(leagueId, {
        leagueName: leagueData.leagueName,
        leagueUrl: leagueData.leagueUrl,
        leagueSize: leagueData.leagueSize,
        platform: leagueData.platform,
        bannerImage: imagePreview ? bannerImageUrl : null,
        isPublic:
          parseInt(leagueData.leagueSize) > teamJoined ? false : isPublic,
      });

      if (leagueFunds) {
        const leagueFundNew = calculateLeageueFunds(leagueData.leagueSize);

        await updateLeagueFundsDistribution(
          leagueId,
          leagueFundNew.distribution,
          leagueFundNew.totalPot
        );
      }

      setIsSubmit(false);
      navigate("/" + leagueData.leagueUrl);
    } else {
      leagueData.leagueUrl = finalSlug;
      leagueData.isPublic = false;
      let modifiedLeagueData = {
        ...leagueData,
        // add commissioner as first team
        teams: [
          {
            createdBy: user?.uid,
            name: "Commissioner",
            joinedAt: new Date().toISOString(),
            isDuePaid: false,
          },
        ],
        // for tracking league creation steps
        leagueProgressTracker: true,
      };
      const res = await addLeagueToFirestore(modifiedLeagueData);
      // if (res.id) {
      //   await updateLeagueProgressStep(res.id, "add-team");
      // }
      console.log(res);
      setIsSubmit(false);
      navigate("/" + finalSlug);
    }
  };

  /**
   * On Edit if leagueSize changes, recalculate league funds
   * @param size League Size
   * @returns Calculated league funds based on size (entry fee, total pot, etc
   */
  const calculateLeageueFunds = (size: string) => {
    let distribution = leagueFunds.distribution ? leagueFunds.distribution : [];
    const entryFee = leagueFunds.entryFee || 0;
    let totalPot = parseInt(size) * entryFee;

    let newDistribution = distribution.map((d: any) => ({
      place: d.place,
      percentage: d.percentage,
      amount: (totalPot * d.percentage) / 100,
    }));
    return { totalPot, distribution: newDistribution };
  };

  /**
   * Image Upload & Cropper Handlers
   */
  const handleImageChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const validationError = validateImageFile(file);
      if (validationError) {
        alert(`${validationError}`);
        return;
      }
      setLoadingCropper(true);
      try {
        const { resizedUrl } = await resizeImage(file);
        setTempImageUrl(resizedUrl);
        setShowCropper(true);
      } finally {
        setLoadingCropper(false);
      }
    },
    []
  );

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!tempImageUrl || !croppedArea) return;
    const cropped = await cropImageFile(tempImageUrl, croppedArea);
    if (cropped) {
      setLeagueImage(cropped);
      setImagePreview(URL.createObjectURL(cropped));
    }
    setShowCropper(false);
  }, [tempImageUrl, croppedArea]);

  const handleRemoveImage = () => {
    setLeagueImage(null);
    setImagePreview(null);
    if (leagueImageRef.current) leagueImageRef.current.value = "";
  };

  const isFormValid =
    leagueName.trim() !== "" &&
    leagueUrl.trim() !== "" &&
    leagueSize.trim() !== "" &&
    platform.trim() !== "" &&
    Object.values(errors).every((err) => !err);

  return (
    <form className="pageWrapper haveFloatingBtn" onSubmit={handleSubmit}>
      <div className="pageHeading">
        {!leagueId && "Creating Your League"}
        {leagueId && "Updating Your League"}
        <span
          className="closeIcon"
          onClick={() => (leagueSlug ? navigate(-1) : navigate("/dashboard"))}
        >
          <CloseIcon />
        </span>
      </div>

      <div className="formGroup">
        <label htmlFor="leagueName">
          League Name <span className="reqTxt">Required</span>
        </label>
        <input
          type="text"
          id="leagueName"
          placeholder="Enter your league name"
          value={leagueName}
          onChange={handleLeagueNameChange}
          maxLength={100}
        />
        {errors.leagueName && (
          <div className="errorText">{errors.leagueName}</div>
        )}
      </div>
      {leagueId && (
        <div className="formGroup">
          <label htmlFor="leagueUrl">
            League Slug <span className="reqTxt">Required</span>
          </label>
          <input
            type="text"
            id="leagueUrl"
            placeholder="Enter your league slug"
            value={leagueUrl}
            onChange={handleLeagueUrlChange}
          />
          {errors.leagueUrl && (
            <div className="errorText">{errors.leagueUrl}</div>
          )}
        </div>
      )}

      <div className="formGroup">
        <label htmlFor="leagueSize">
          League Size <span className="reqTxt">Required</span>
        </label>
        <select
          id="leagueSize"
          className="formSelect"
          value={leagueSize}
          onChange={handleLeagueSizeChange}
        >
          <option value="2">2 Teams</option>
          <option value="4">4 Teams</option>
          <option value="6">6 Teams</option>
          <option value="8">8 Teams</option>
          <option value="10">10 Teams</option>
          <option value="12">12 Teams</option>
          <option value="14">14 Teams</option>
          <option value="16">16 Teams</option>
          <option value="18">18 Teams</option>
          <option value="20">20 Teams</option>
        </select>
        {errors.leagueSize && (
          <div className="errorText">{errors.leagueSize}</div>
        )}
      </div>

      <div className="platformWrap">
        <div className="headingBlk">Fantasy Platform</div>
        <ul>
          <li
            onClick={() => handlePlatformSelect("Yahoo")}
            className={platform === "Yahoo" ? "active" : ""}
          >
            <YahooLogo />
          </li>
          <li
            onClick={() => handlePlatformSelect("ESPN")}
            className={platform === "ESPN" ? "active" : ""}
          >
            <ESPNLogo />
          </li>
          <li
            onClick={() => handlePlatformSelect("Sleeper")}
            className={platform === "Sleeper" ? "active" : ""}
          >
            <img src={sleeperLogo} alt="Sleeper" />
          </li>
          <li
            onClick={() => handlePlatformSelect("NFL")}
            className={platform === "NFL" ? "active" : ""}
          >
            <NFLLogo />
          </li>
          <li
            onClick={() => handlePlatformSelect("RedZone")}
            className={platform === "RedZone" ? "active" : ""}
          >
            <RedZoneLogo />
          </li>
          <li
            onClick={() => handlePlatformSelect("Not Listed")}
            className={platform === "Not Listed" ? "active" : ""}
          >
            Not Listed
          </li>
        </ul>
      </div>
      {leagueId && (
        <div className="visibilityWrap">
          <div className="headingBlk">League Visibility</div>
          <div
            className={`switchWrap ${
              teamJoined !== parseInt(leagueSize) ? "switchDisabled" : ""
            }`}
          >
            <label>Public League</label>
            <label className="switchOption">
              <input
                type="checkbox"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
              />
              <span className={`customCheck ${isPublic ? "haveChecked" : ""}`}>
                <span
                  className="checkCircle"
                  style={{
                    transform: isPublic ? "translateX(26px)" : "translateX(0)",
                  }}
                />
              </span>
            </label>
          </div>
        </div>
      )}
      {showCropper && tempImageUrl && (
        <div className="popupWrapper fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
          <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
            <Cropper
              image={tempImageUrl}
              crop={crop}
              zoom={zoom}
              aspect={16 / 9}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
              <button
                type="button"
                onClick={() => setShowCropper(false)}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCropConfirm}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
              >
                Crop & Save
              </button>
            </div>
          </div>
        </div>
      )}
      {leagueId && (
        <div className="uploadArea topGap1">
          {!imagePreview && (
            <>
              <input
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                ref={leagueImageRef}
                onChange={handleImageChange}
              />
              <button
                className="btnStyle btnHighlighted"
                type="button"
                onClick={() => leagueImageRef.current?.click()}
              >
                UPLOAD LEAGUE BANNER
              </button>
            </>
          )}

          {imagePreview && (
            <div className="mt-2 relative inline-block">
              <img
                src={imagePreview}
                alt="Punishment Preview"
                style={{ maxWidth: 300, borderRadius: 8 }}
              />
              <button
                type="button"
                onClick={() => {
                  handleRemoveImage();
                }}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                style={{ lineHeight: 1, fontSize: 16 }}
                aria-label="Remove image"
              >
                ×
              </button>
            </div>
          )}
        </div>
      )}

      <button
        type="submit"
        className="btnStyle floatingBtn"
        disabled={!isFormValid || isSubmit}
      >
        Continue
      </button>
    </form>
  );
};

export default CreateLeaguePage;
